import React, { useState, useEffect, useRef } from 'react';
import { 
  Activity, ShoppingCart, Package, Trash2, User, 
  Settings, AlertTriangle, RefreshCw, Filter,
  Download, Clock, ChevronDown, ChevronUp, Wallet, Calculator
} from 'lucide-react';
import api from '../api/axios';
import { formatDate, formatTime } from '../utils/dateFormat';

const ACTIVITY_TYPES = [
  { value: 'all', label: 'All Activities', icon: Activity },
  { value: 'sale', label: 'Sales', icon: ShoppingCart },
  { value: 'inventory', label: 'Inventory', icon: Package },
  { value: 'wastage', label: 'Wastage', icon: Trash2 },
  { value: 'expense', label: 'Expenses', icon: Wallet },
  { value: 'reconciliation', label: 'Reconciliation', icon: Calculator },
  { value: 'user', label: 'Users', icon: User },
  { value: 'system', label: 'System', icon: Settings },
];

const getActivityIcon = (type) => {
  switch (type) {
    case 'sale': return <ShoppingCart className="w-4 h-4 text-green-500" />;
    case 'inventory': return <Package className="w-4 h-4 text-blue-500" />;
    case 'wastage': return <Trash2 className="w-4 h-4 text-red-500" />;
    case 'user': return <User className="w-4 h-4 text-purple-500" />;
    case 'expense': return <Wallet className="w-4 h-4 text-orange-500" />;
    case 'reconciliation': return <Calculator className="w-4 h-4 text-orange-500" />;
    default: return <Settings className="w-4 h-4 text-gray-500" />;
  }
};

const getActivityColor = (type, actionType) => {
  if (actionType === 'alert') return 'border-l-yellow-500 bg-yellow-50';
  if (actionType === 'deleted') return 'border-l-red-500 bg-red-50';
  
  switch (type) {
    case 'sale': return 'border-l-green-500';
    case 'inventory': return 'border-l-blue-500';
    case 'wastage': return 'border-l-red-500';
    case 'user': return 'border-l-purple-500';
    case 'expense': return 'border-l-orange-500';
    case 'reconciliation': return 'border-l-orange-500';
    default: return 'border-l-gray-500';
  }
};

const formatActivityDate = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) {
    return 'Today';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return formatDate(timestamp);
  }
};

function LiveActivityPanel({ isFullPage = false }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [isExpanded, setIsExpanded] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const pollInterval = useRef(null);

  const fetchActivities = useRef(null);

  fetchActivities.current = async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '50');
      if (filterType !== 'all') {
        params.append('type', filterType);
      }
      
      const response = await api.get(`/activity/feed/?${params.toString()}`);
      setActivities(response.data.activities || []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to fetch activities');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities.current();
    
    if (autoRefresh) {
      pollInterval.current = setInterval(() => fetchActivities.current(), 10000);
    }
    
    return () => {
      if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
    };
  }, [filterType, autoRefresh]);

  const toggleAutoRefresh = () => {
    setAutoRefresh(prev => {
      if (!prev) {
        pollInterval.current = setInterval(() => fetchActivities.current(), 10000);
      } else if (pollInterval.current) {
        clearInterval(pollInterval.current);
      }
      return !prev;
    });
  };

  // Group activities by date
  const groupedActivities = activities.reduce((groups, activity) => {
    const dateKey = formatActivityDate(activity.created_at);
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(activity);
    return groups;
  }, {});

  if (!isFullPage) {
    // Compact sidebar/dashboard version
    return (
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div 
          className="bg-gradient-to-r from-red-500 to-orange-500 px-4 py-3 flex justify-between items-center cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center gap-2 text-white">
            <div className="relative">
              <Activity className="w-5 h-5" />
              {autoRefresh && (
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              )}
            </div>
            <span className="font-semibold">Live Activity</span>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
        </div>
        
        {isExpanded && (
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mx-auto mb-2" />
                Loading...
              </div>
            ) : activities.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No recent activity
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {activities.slice(0, 10).map(activity => (
                  <div 
                    key={activity.id} 
                    className={`px-4 py-3 border-l-4 ${getActivityColor(activity.activity_type, activity.action_type)}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {getActivityIcon(activity.activity_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-800 truncate">{activity.action}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-gray-500">{activity.user_name || 'System'}</span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500">{formatTime(activity.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {lastUpdate && (
              <div className="px-4 py-2 bg-gray-50 text-xs text-gray-500 flex items-center justify-between">
                <span>Updated: {formatTime(lastUpdate)}</span>
                <button 
                  onClick={(e) => { e.stopPropagation(); fetchActivities.current(); }}
                  className="text-orange-600 hover:text-orange-700"
                >
                  Refresh
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Full page version
  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800 flex items-center gap-2">
            <div className="relative">
              <Activity className="w-6 h-6 sm:w-7 sm:h-7 text-red-500" />
              {autoRefresh && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
              )}
            </div>
            🔴 Live Activity Feed
          </h1>
          <p className="text-sm sm:text-base text-gray-500">Real-time activity monitoring</p>
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={toggleAutoRefresh}
            className={`flex items-center gap-1 sm:gap-2 px-3 py-2 rounded-lg text-sm ${
              autoRefresh 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{autoRefresh ? 'Auto-Refresh ON' : 'Auto-Refresh OFF'}</span>
            <span className="sm:hidden">{autoRefresh ? 'Auto' : 'Manual'}</span>
          </button>
          
          <button
            onClick={() => fetchActivities.current()}
            className="flex items-center gap-1 sm:gap-2 px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
          >
            <RefreshCw className="w-4 h-4" />
            <span className="hidden sm:inline">Refresh Now</span>
            <span className="sm:hidden">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <span className="text-gray-600 mr-2">Filter:</span>
          {ACTIVITY_TYPES.map(type => {
            const Icon = type.icon;
            return (
              <button
                key={type.value}
                onClick={() => setFilterType(type.value)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition ${
                  filterType === type.value
                    ? 'bg-orange-100 text-orange-700 border border-orange-300'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
                title={type.label}
              >
                <Icon className="w-4 h-4" />
                {type.value === 'all' ? <span>{type.label}</span> : <span className="hidden sm:inline">{type.label}</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Activity Feed */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-gray-500">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
            Loading activities...
          </div>
        ) : activities.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium">No activities found</p>
            <p className="text-sm">Activities will appear here as they happen</p>
          </div>
        ) : (
          <div>
            {Object.entries(groupedActivities).map(([date, dateActivities]) => (
              <div key={date}>
                <div className="sticky top-0 bg-gray-100 px-4 py-2 border-b">
                  <span className="text-sm font-medium text-gray-600">{formatDate(date)}</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {dateActivities.map(activity => (
                    <div 
                      key={activity.id} 
                      className={`px-4 py-4 border-l-4 hover:bg-gray-50 transition ${
                        getActivityColor(activity.activity_type, activity.action_type)
                      }`}
                    >
                      <div className="flex items-start gap-3 sm:gap-4">
                        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-full flex items-center justify-center">
                          {getActivityIcon(activity.activity_type)}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              activity.activity_type === 'sale' ? 'bg-green-100 text-green-700' :
                              activity.activity_type === 'inventory' ? 'bg-blue-100 text-blue-700' :
                              activity.activity_type === 'wastage' ? 'bg-red-100 text-red-700' :
                              activity.activity_type === 'user' ? 'bg-purple-100 text-purple-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {activity.activity_type?.toUpperCase()}
                            </span>
                            {activity.action_type === 'alert' && (
                              <AlertTriangle className="w-4 h-4 text-yellow-500" />
                            )}
                          </div>
                          
                          <p className="text-gray-800 font-medium">{activity.action}</p>
                          
                          {activity.details && (
                            <p className="text-sm text-gray-600 mt-1">{activity.details}</p>
                          )}
                          
                          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <User className="w-3 h-3" />
                              {activity.user_name || 'System'}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatTime(activity.created_at)}
                            </span>
                            {activity.module && (
                              <span className="bg-gray-100 px-2 py-0.5 rounded">
                                {activity.module}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Status Bar */}
      {lastUpdate && (
        <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
          <span>
            Showing {activities.length} activities
            {autoRefresh && ' • Auto-refreshing every 10 seconds'}
          </span>
          <span>Last updated: {formatTime(lastUpdate)}</span>
        </div>
      )}
    </div>
  );
}

export default LiveActivityPanel;
